import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api';
import EditClient from '../../EditClient';
import EntityInfo from '../../Bookkeeping/EntityInfo';
import type { AwLogin, AwNumberingSeries, AwProfile, AwRegisterSeries, AwSeriesScheme } from '../../../types';
import type { AwCtx } from '../ctx';
import { btn, card, dmy, errBox, inp, note, pillAmber, pillGreen, pillGrey, sectionHead, table, td, th } from '../ui';
import { isReadOnlySeat } from './fixedAssets/shared';

// ── Settings panes (owner 2026-10-13) ───────────────────────────────────────────────────────────
// Company profile and Tax settings read the firm's CLIENT record (clients row) — the merchant's own
// companies row is portal-owned and invisible in Admin — and edit it through the existing EditClient
// modal, so there is one form for a client's identity, not two. Document numbering and portal logins
// are the merchant's: shown read-only, never the logo, with the write paths staying in the portal /
// Portal Accounts where accountability for them already sits.

type P = AwProfile['client'];
const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div style={{ display: 'contents' }}>
    <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)', padding: '5px 0' }}>{label}</div>
    <div style={{ fontSize: 13, color: 'var(--text-primary)', padding: '5px 0', wordBreak: 'break-word' }}>{value === '' || value === null || value === undefined ? '—' : value}</div>
  </div>
);
const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(140px, 200px) 1fr', columnGap: 16 };

function useProfile(ctx: AwCtx) {
  const [p, setP] = useState<P | null>(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => {
    api.awProfile(ctx.clientId).then((d: AwProfile) => { setP(d.client); setErr(''); }).catch((e: any) => setErr(e.message));
  }, [ctx.clientId]);
  useEffect(() => { load(); }, [load, ctx.reload]);
  return { p, err, load };
}

function ProfileCard({ ctx, fields, caption }: { ctx: AwCtx; fields: 'all' | 'tax'; caption?: string }) {
  const { t } = useTranslation();
  const { p, err, load } = useProfile(ctx);
  const [editing, setEditing] = useState(false);
  // ⛔ No Edit for a read-only seat (a client's own books login, the auditor). The client-record editor is
  // staff-only by construction: it reads /api/services and /api/clients, which a books login may not reach
  // (middleware.ts BOOKS_PREFIXES), so even OPENING it would send three refused calls, each paging the admins.
  const ro = isReadOnlySeat(ctx);
  const yn = (v: number | null) => v === null || v === undefined ? '—' : v === 1 ? t('aw_yes') : t('aw_no');
  const fy = (m: number | null, d: number | null) => m ? `${d || 1}/${m}` : '—';
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)', flex: 1 }}>{p?.company_name || ctx.clientName}</div>
        {!ro && <button onClick={() => setEditing(true)} style={btn}><i className="ti ti-pencil" aria-hidden /> {t('aw_profile_edit')}</button>}
      </div>
      {err && <div style={{ ...errBox, marginBottom: 8 }}>{err}</div>}
      {!p ? <i className="ti ti-loader" style={{ color: 'var(--text-tertiary)' }} /> : (
        <div style={grid}>
          {fields === 'all' && <>
            <Row label={t('aw_pf_company_name')} value={p.company_name} />
            <Row label={t('aw_pf_client_name')} value={p.client_name} />
            <Row label={t('aw_pf_client_no')} value={p.client_no} />
            <Row label={t('aw_pf_entity_type')} value={p.entity_type} />
            <Row label={t('aw_pf_juristic_form')} value={p.juristic_form} />
            {/* Registered capital is free text on the clients row ("1,000,000.00 THB" as printed on the
                affidavit) — shown as stored. Running it through money() printed 0.00 for every real value. */}
            <Row label={t('aw_pf_registered_capital')} value={p.registered_capital} />
            <Row label={t('aw_pf_address')} value={p.address} />
            <Row label={t('aw_pf_email')} value={p.email} />
            <Row label={t('aw_pf_phone')} value={p.phone} />
          </>}
          <Row label={t('aw_pf_tax_id')} value={p.tax_id} />
          <Row label={t('aw_pf_branch_no')} value={p.branch_no} />
          <Row label={t('aw_pf_has_vat')} value={yn(p.has_vat)} />
          <Row label={t('aw_pf_vat_no')} value={p.vat_no} />
          <Row label={t('aw_pf_vat_reg_date')} value={p.vat_reg_date ? dmy(p.vat_reg_date) : ''} />
          <Row label={t('aw_pf_fy_start')} value={fy(p.fy_start_month, p.fy_start_day)} />
        </div>
      )}
      {caption && <div style={{ ...note, marginTop: 10 }}><i className="ti ti-info-circle" aria-hidden /> {caption}</div>}
      {editing && !ro && <EditClient clientId={ctx.clientId} onClose={() => setEditing(false)} onSaved={() => { load(); ctx.bump(); }} />}
    </div>
  );
}

export function CompanyProfilePane({ ctx }: { ctx: AwCtx }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ProfileCard ctx={ctx} fields="all" />
      <EntityInfo clientId={ctx.clientId} periodClosed={ctx.periodClosed} onChanged={ctx.bump} />
    </div>
  );
}

export function TaxSettingsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <ProfileCard ctx={ctx} fields="tax" caption={t('aw_tax_settings_caption')} />
      <FxTaxRuleCard ctx={ctx} />
      <FxRatesCard ctx={ctx} />
    </div>
  );
}

/** ── The published rates, and the holes that stop a booking (owner 2026-10-16) ─────────────────
 *  The second leg of the two-leg rule needs the quote of a MONTH'S LAST BUSINESS DAY, exactly. So
 *  the question an operator has is not "what rates do we hold" but "which months can I not book",
 *  and that is what this shows — for the currencies this client actually issues in, and no others.
 *
 *  ⛔ The source is required, and it is not decoration: services/fxRevaluation.ts refuses a rate
 *  with no stated source, because a rate nobody can point at cannot be defended to an inspector.
 */
function FxRatesCard({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [cov, setCov] = useState<any>(null);
  const [text, setText] = useState('');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  // A read-only seat reads the coverage table; the import (a POST) is a writer's, so it is left out.
  const ro = isReadOnlySeat(ctx);
  const load = useCallback(() => {
    api.acctFxCoverage(ctx.clientId).then((d: any) => setCov(d)).catch((e: any) => setErr(e.message));
  }, [ctx.clientId]);
  useEffect(() => { load(); }, [load, ctx.reload]);

  /** One line = one rate: date, currency, rate — and optionally the kind. ⛔ Parsed here and sent as
   *  rows, because the server's importer is the thing that validates and de-duplicates them; this
   *  only turns text into rows. */
  const parse = (): { rows: any[]; bad: number } => {
    const rows: any[] = []; let bad = 0;
    for (const raw of text.split(/\r?\n/)) {
      const line = raw.trim();
      if (!line) continue;
      const c = line.split(/[,;\t]/).map(x => x.trim()).filter(x => x !== '');
      if (c.length < 3) { bad++; continue; }
      const [date, currency, third, fourth] = c;
      const kindGiven = /^bot_(buying|selling|average)$/.test(String(third || '')) ? third : (/^bot_(buying|selling|average)$/.test(String(fourth || '')) ? fourth : '');
      const rate = Number(kindGiven === third ? fourth : third);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^[A-Za-z]{3}$/.test(currency) || !(rate > 0)) { bad++; continue; }
      rows.push({ rateDate: date, currency: currency.toUpperCase(), kind: kindGiven || 'bot_buying', rate });
    }
    return { rows, bad };
  };
  const doImport = async () => {
    const { rows, bad } = parse();
    if (!rows.length) { setErr(t('aw_fx_imp_none', { bad })); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      const r: any = await api.acctFxImport(rows, source.trim());
      setMsg(t('aw_fx_imp_done', { n: r.imported, skipped: (r.skipped || []).length, bad }));
      setText(''); load();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };

  const missing = (cov?.rows || []).filter((r: any) => !r.have);
  return (
    <div style={card}>
      <div style={sectionHead}>{t('aw_fx_rates_head')}</div>
      {!cov ? <div style={note}>…</div> : !cov.currencies.length ? (
        <div style={note}>{t('aw_fx_rates_none_needed', { home: cov.home })}</div>
      ) : (
        <>
          <div style={{ ...note, marginBottom: 8 }}>{t('aw_fx_rates_hint', { cur: cov.currencies.join(', ') })}</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={table}>
              <thead><tr>
                <th style={th}>{t('aw_bank_rc_month')}</th>
                <th style={th}>{t('aw_col_currency')}</th>
                <th style={th}>{t('aw_fx_rates_day')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_fx_rates_rate')}</th>
              </tr></thead>
              <tbody>
                {(cov.rows || []).slice(0, 36).map((r: any) => (
                  <tr key={`${r.currency}-${r.ym}`} style={{ background: r.have ? undefined : '#FFFBEB' }}>
                    <td style={td}>{r.ym}</td>
                    <td style={td}>{r.currency}</td>
                    <td style={td}>{r.asked || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.have ? undefined : '#B45309' }}>
                      {r.have ? Number(r.rate).toFixed(6)
                        : r.nearestOnly ? t('aw_fx_rates_nearest', { rate: Number(r.rate).toFixed(6), date: r.rateDate })
                          : t('aw_fx_rates_missing')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!!missing.length && <div style={{ ...note, color: '#B45309', marginTop: 6 }}>{t('aw_fx_rates_blocked', { n: missing.length })}</div>}
        </>
      )}
      {!ro && <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t('aw_fx_imp_head')}</div>
        <div style={{ ...note, marginBottom: 6 }}>{t('aw_fx_imp_hint')}</div>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={5} placeholder={t('aw_fx_imp_ph')}
          style={{ ...inp, width: '100%', fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
          <input value={source} onChange={e => setSource(e.target.value)} placeholder={t('aw_fx_imp_source_ph')}
            style={{ ...inp, flex: '1 1 260px', maxWidth: 420 }} />
          <button onClick={doImport} disabled={busy || !text.trim() || !source.trim()}
            style={{ ...btn, opacity: (busy || !text.trim() || !source.trim()) ? 0.5 : 1 }}>
            <i className={busy ? 'ti ti-loader' : 'ti ti-download'} aria-hidden /> {t('aw_fx_imp_btn')}
          </button>
        </div>
      </div>}
      {msg && <div style={{ fontSize: 12.5, color: '#166534', marginTop: 8 }}>{msg}</div>}
      {err && <div style={errBox}>{err}</div>}
    </div>
  );
}

/** ── Which baht figure a foreign-currency document is taxed on (owner 2026-10-16) ──────────────
 *  ⛔ Off for every client until somebody turns it on here: it changes a VAT base, and the rule is
 *  one client's own policy, not the product's opinion. The two legs are a question of FACT — was
 *  the money converted inside the month — so nothing here asks for a number; the bank's own
 *  statement line answers the first leg and the published rate answers the second. */
function FxTaxRuleCard({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [rule, setRule] = useState<'document' | 'two_leg'>('document');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // The rule is saved the moment the select changes, so a read-only seat is shown the rule as text instead.
  const ro = isReadOnlySeat(ctx);
  useEffect(() => {
    let alive = true;
    api.acctFxTaxRule(ctx.clientId).then((d: any) => { if (alive) setRule(d.rule === 'two_leg' ? 'two_leg' : 'document'); })
      .catch(() => { if (alive) setRule('document'); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);
  const save = async (next: 'document' | 'two_leg') => {
    setBusy(true); setErr('');
    try { const d: any = await api.acctSetFxTaxRule(ctx.clientId, next); setRule(d.rule); }
    catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div style={card}>
      <div style={sectionHead}>{t('aw_fx_rule_head')}</div>
      {ro ? (
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }} data-fx-rule={rule}>{t(rule === 'two_leg' ? 'aw_fx_rule_two_leg' : 'aw_fx_rule_document')}</div>
      ) : (
        <select value={rule} disabled={busy} onChange={e => save(e.target.value as 'document' | 'two_leg')} style={{ ...inp, maxWidth: 430 }}>
          <option value="document">{t('aw_fx_rule_document')}</option>
          <option value="two_leg">{t('aw_fx_rule_two_leg')}</option>
        </select>
      )}
      <div style={{ ...note, marginTop: 8 }}>{t(rule === 'two_leg' ? 'aw_fx_rule_two_leg_note' : 'aw_fx_rule_document_note')}</div>
      {err && <div style={errBox}>{err}</div>}
    </div>
  );
}

const SERIES_KEY: Record<string, string> = { inv: 'aw_series_inv', quo: 'aw_series_quo', rec: 'aw_series_rec', tax: 'aw_series_tax', pro: 'aw_series_pro', cn: 'aw_series_cn', dn: 'aw_series_dn',
  po: 'aw_series_po', claim: 'aw_series_claim', exp: 'aw_series_exp', pmt: 'aw_series_pmt' };
const RESET_KEY: Record<string, string> = { none: 'p_prof_reset_none', year: 'p_prof_reset_year', month: 'p_prof_reset_month', day: 'p_prof_reset_day' };

export function NumberingPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  // The scheme is an OBJECT ({ date, reset, pad } — services/docNumberScheme.ts), never a string:
  // rendered as a child it took the whole React tree down. Spelt out with the portal's own words for
  // the same three settings (p_prof_*), so the merchant and the firm read one description.
  const schemeText = (s: AwSeriesScheme | null | undefined): string => {
    if (!s || typeof s !== 'object') return s ? String(s) : '—';
    const date = s.date === 'none' || !s.date ? t('p_prof_date_none') : s.date;
    // EXP / PMT written without dashes (owner 2026-10-28, EXP-8 — EXP202609150002) say so; every other scheme reads as before.
    return `${date} · ${t(RESET_KEY[s.reset] || RESET_KEY.none)} · ${t('aw_scheme_pad', { n: s.pad })}${s.sep === '' ? ` · ${t('aw_scheme_nosep')}` : ''}`;
  };
  const [rows, setRows] = useState<AwNumberingSeries[] | null>(null);
  // The register series (batch 7 R2b) — purchase orders, claims, expense records, supplier payments: numbered by this
  // workspace; `next` is the issuing function's own dry run. Read-only here, for every seat (owner 2026-10-28, EXP-8):
  // the client company sets the EXP and PMT format itself in its portal; purchase orders and claims nobody edits.
  const [reg, setReg] = useState<AwRegisterSeries[]>([]);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.awNumbering(ctx.clientId).then((d: any) => { if (alive) { setRows(d.series || []); setReg(Array.isArray(d.register) ? d.register : []); } })
      .catch((e: any) => { if (alive) { setRows([]); setReg([]); setErr(e.message); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {rows === null ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div> : !rows.length ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('aw_no_rows')}</div> : (
          <table style={table}>
            <thead><tr><th style={th}>{t('aw_numbering_series')}</th><th style={th}>{t('aw_numbering_prefix')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_numbering_start')}</th><th style={th}>{t('aw_numbering_scheme')}</th><th style={th}>{t('aw_numbering_month')}</th><th style={th}>{t('aw_numbering_next')}</th></tr></thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.key}>
                  <td style={td}>{SERIES_KEY[s.key] ? t(SERIES_KEY[s.key]) : s.key}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{s.prefix}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.start}</td>
                  <td style={td}>{schemeText(s.scheme)}</td>
                  <td style={td}>{s.monthSegment ? t('aw_yes') : t('aw_no')}</td>
                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{s.preview}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={note}>{t('aw_numbering_note')}</div>
      {reg.length > 0 && <>
        <div style={sectionHead}>{t('aw_numbering_register_head')}</div>
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          <table style={table} data-numbering="register">
            <thead><tr><th style={th}>{t('aw_numbering_series')}</th><th style={th}>{t('aw_numbering_prefix')}</th><th style={th}>{t('aw_numbering_scheme')}</th><th style={th}>{t('aw_numbering_next')}</th></tr></thead>
            <tbody>
              {reg.map(s => (
                <tr key={s.key} data-series={s.key}>
                  <td style={td}>{SERIES_KEY[s.key] ? t(SERIES_KEY[s.key]) : s.key}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{s.prefix}</td>
                  <td style={td}>{schemeText(s.scheme)}</td>
                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{s.next}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={note}>{t('aw_numbering_register_note')}</div>
      </>}
    </div>
  );
}

export function LoginsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<AwLogin[] | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.awLogins(ctx.clientId).then((d: any) => { if (alive) setRows(d.rows || []); }).catch((e: any) => { if (alive) { setRows([]); setErr(e.message); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);
  const canManage = ctx.me.modules.includes('portal_accounts');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {rows === null ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div> : !rows.length ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('aw_logins_none')}</div> : (
          <table style={table}>
            <thead><tr><th style={th}>{t('aw_logins_email')}</th><th style={th}>{t('aw_logins_name')}</th><th style={th}>{t('aw_logins_active')}</th><th style={th}>{t('aw_logins_must_change')}</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={td}>{r.email}</td><td style={td}>{r.name}</td>
                  <td style={td}><span style={r.active ? pillGreen : pillGrey}>{r.active ? t('aw_yes') : t('aw_no')}</span></td>
                  <td style={td}>{r.must_change_password ? <span style={pillAmber}>{t('aw_yes')}</span> : <span style={pillGrey}>{t('aw_no')}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={sectionHead}>{t('aw_col_actions')}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {/* Account creation / reset / disable stay in Portal Accounts (its own module) — a link, never a re-implementation. */}
        {canManage && (
          <a href="/m/portal_accounts" target="_blank" rel="noreferrer" style={{ ...btn, textDecoration: 'none' }}>
            <i className="ti ti-users" aria-hidden /> {t('aw_logins_manage')} <i className="ti ti-external-link" style={{ fontSize: 12 }} aria-hidden />
          </a>
        )}
        <div style={note}>{t('aw_logins_note')}</div>
      </div>
    </div>
  );
}
